import { toRaw } from 'vue';
import type { AxiosError } from 'axios';
import axiosInstance from '@/services/interceptor.services';
import type {
  IHorraireCommerceResponse,
  INewScheduleItem,
  IWeekdayScheduleBody,
} from '@/types/horraire.commerce.types';
import { restaurantId } from '@/refs/common.refs';
import {
  HorraireCommerce_id,
  HorraireCommerce_page,
} from '@/refs/horraire.commerce.refs';
import { commerceIdHorraire } from '@/refs/commerce.refs';

export const HorraireCommerceService = {
  /**
   * Crée une nouvelle HorraireCommerce dans le système.
   *
   * @async
   * @function createHorraireCommerce
   * @param {INewScheduleItem} data - Données de la HorraireCommerce à enregistrer.
   * @returns {Promise<any>} La réponse de l'API après la création de la HorraireCommerce.
   * @throws {AxiosError} Lance une erreur si la requête échoue.
   */
  createHorraireCommerce: async (data: any) => {
    try {
      const response = await axiosInstance.post(
        `restaurants/${commerceIdHorraire.value}/schedules/add`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère la liste paginée des HorraireCommerces.
   *
   * @async
   * @function getHorraireCommerce
   * @returns {Promise<IHorraireCommerceResponse>} Données paginées des HorraireCommerces retournées par l'API.
   * @throws {AxiosError} Lance une erreur si la requête échoue.
   */
  getHorraireCommerce: async () => {
    try {
      const page = Number(toRaw(HorraireCommerce_page.value));

      const params = new URLSearchParams();
      params.append('pagination', 'true');
      params.append('page', page.toString());

      const response = await axiosInstance.get<IHorraireCommerceResponse>(
        `restaurants/${restaurantId.value}/schedules/search?${params.toString()}`,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère une HorraireCommerce spécifique à partir de son identifiant.
   *
   * @async
   * @function getHorraireCommerceById
   * @returns {Promise<any | undefined>}
   *          Retourne les données de la HorraireCommerce depuis l'API,
   *          ou `undefined` si `HorraireCommerce_id` est null.
   * @throws {AxiosError} Lance une erreur si la requête HTTP échoue.
   */
  getHorraireCommerceById: async () => {
    try {
      if (HorraireCommerce_id.value === null) return;
      const id = toRaw(HorraireCommerce_id.value.value);
      const response = await axiosInstance.get<any>(
        `HorraireCommerces/get/${id}`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },
  /**
   * Met à jour une HorraireCommerce existante par son identifiant.
   *
   * @async
   * @function INewScheduleItem
   * @param {Partial<INewScheduleItem>} HorraireCommerceData - Données partielles de la HorraireCommerce à mettre à jour.
   * @returns {Promise<any | undefined>}
   *          Réponse de l'API après la mise à jour, ou `undefined` si `HorraireCommerce_id` est null.
   * @throws {AxiosError} Lève une erreur si la requête échoue.
   */
  updateHorraireCommerce: async (
    HorraireCommerceData: Partial<INewScheduleItem>,
  ) => {
    try {
      if (HorraireCommerce_id.value === null) return;

      const id = toRaw(HorraireCommerce_id.value);

      const response = await axiosInstance.put<any>(
        `restaurants/${restaurantId.value}/schedules/update/${id}`,
        HorraireCommerceData,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Met à jour les horaires hebdomadaires d'un commerce (weekday-schedule).
   * PUT restaurants/{storeId}/schedules/weekday-schedule
   */
  updateWeekdaySchedule: async (
    storeId: string,
    data: IWeekdayScheduleBody,
  ) => {
    try {
      const response = await axiosInstance.put(
        `restaurants/${storeId}/schedules/weekday-schedule`,
        data,
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime une HorraireCommerce existante par son identifiant.
   *
   * @async
   * @function deleteHorraireCommerce
   * @returns {Promise<any | undefined>}
   *          Réponse de l'API après suppression, ou `undefined` si `HorraireCommerce_id` est null.
   * @throws {AxiosError} Lève une erreur si la requête HTTP échoue.
   */
  deleteHorraireCommerce: async () => {
    try {
      if (HorraireCommerce_id.value === null) return;

      const id = toRaw(HorraireCommerce_id.value);

      const response = await axiosInstance.delete(
        `restaurants/${restaurantId.value}/schedules/delete/${id}`,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },
};
